// context/AuthProvider.tsx
import React, { createContext, SetStateAction, useContext, useEffect, useState } from "react";
import { auth } from "../firebase";
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  UserCredential
} from "firebase/auth";
import axios from "axios";
import { BASE_URL } from "../utils/Scripts";


type ExtendedUser = User & {
  roles: number[];
  verified: boolean;
};

type AuthContextType = {
  user: ExtendedUser | null;
  loading: boolean;
  signup: (email: string, password: string) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  setUser: React.Dispatch<SetStateAction<ExtendedUser|null>>
};

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => useContext(AuthContext)!;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if(firebaseUser){
        const idToken = await firebaseUser.getIdToken(); // token JWT
        const response = await axios.get(`${BASE_URL}accounts/refresh/${firebaseUser.uid}`,{
          headers: { Authorization: `Bearer ${idToken}` }
        });
        setUser({...firebaseUser,...{roles:response.data.roles, verified: response.data.verified}});
      } else {
        setUser(null)
      }
      setLoading(false);
    });
    return ()=> unsubscribe();
  }, []);

  const signup = (email: string, password: string) => 
    createUserWithEmailAndPassword(auth, email, password);

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);    
    return userCredential.user;
  };

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ user, loading, signup, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
